import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  getWorkshopContentWithBlocks,
  updateWorkshopContent,
  createWorkshopBlock,
  updateWorkshopBlock,
  deleteWorkshopBlock,
  deleteWorkshopContent,
  reorderWorkshopBlocks
} from '@/lib/db'
import type { WorkshopContent, WorkshopBlock, WorkshopBlockType } from '@/types'
import {
  ArrowLeft, Save, Loader2, Plus, Trash2, GripVertical,
  ChevronDown, ChevronUp, Eye, EyeOff, AlertTriangle, Code
} from 'lucide-react'

const BLOCK_TYPE_LABELS: Record<WorkshopBlockType, string> = {
  title_slide: 'Diapositiva de título',
  vocabulary: 'Vocabulario',
  phrase_list: 'Lista de frases',
  dialogue: 'Diálogo',
  roulette: 'Ruleta interactiva',
  flashcards: 'Flashcards',
  matching: 'Unir pares',
  fill_blanks: 'Completar espacios',
  role_play: 'Juego de roles',
  image_describe: 'Describir imagen',
  poll: 'Encuesta/Quiz',
  timer_activity: 'Actividad con temporizador',
  free_html: 'HTML libre',
  video_embed: 'Video embebido',
  audio_listen: 'Audio para escuchar',
  sentence_builder: 'Construir oraciones',
  conversation_prompts: 'Temas de conversación'
}

const BLOCK_TYPE_COLORS: Record<WorkshopBlockType, string> = {
  title_slide: 'bg-[#0055A4]/10 text-[#0055A4] border-[#0055A4]/20',
  vocabulary: 'bg-green-50 text-green-700 border-green-200',
  phrase_list: 'bg-teal-50 text-teal-700 border-teal-200',
  dialogue: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  roulette: 'bg-red-50 text-red-700 border-red-200',
  flashcards: 'bg-amber-50 text-amber-700 border-amber-200',
  matching: 'bg-orange-50 text-orange-700 border-orange-200',
  fill_blanks: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  role_play: 'bg-pink-50 text-pink-700 border-pink-200',
  image_describe: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  poll: 'bg-violet-50 text-violet-700 border-violet-200',
  timer_activity: 'bg-rose-50 text-rose-700 border-rose-200',
  free_html: 'bg-gray-50 text-gray-600 border-gray-200',
  video_embed: 'bg-purple-50 text-purple-700 border-purple-200',
  audio_listen: 'bg-sky-50 text-sky-700 border-sky-200',
  sentence_builder: 'bg-lime-50 text-lime-700 border-lime-200',
  conversation_prompts: 'bg-emerald-50 text-emerald-700 border-emerald-200'
}

const SECTION_OPTIONS = ['Calentamiento', 'Introducción', 'Vocabulario', 'Práctica guiada', 'Práctica libre', 'Juegos', 'Cierre', 'Bonus']

export default function WorkshopContentEdit() {
  const { contentId } = useParams()
  const navigate = useNavigate()
  const [content, setContent] = useState<WorkshopContent | null>(null)
  const [blocks, setBlocks] = useState<WorkshopBlock[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [expandedBlock, setExpandedBlock] = useState<string | null>(null)
  const [addingBlock, setAddingBlock] = useState(false)
  const [showDeleteContent, setShowDeleteContent] = useState(false)
  const [deletingContent, setDeletingContent] = useState(false)

  const [title, setTitle] = useState('')
  const [titleFr, setTitleFr] = useState('')
  const [description, setDescription] = useState('')
  const [duration, setDuration] = useState(45)
  const [isPublished, setIsPublished] = useState(false)

  useEffect(() => {
    if (!contentId) return
    loadContent()
  }, [contentId])

  async function loadContent() {
    setLoading(true)
    const { data, error } = await getWorkshopContentWithBlocks(contentId!)
    if (data) {
      setContent(data)
      setBlocks(data.blocks || [])
      setTitle(data.title)
      setTitleFr(data.title_en || '')
      setDescription(data.description || '')
      setDuration(data.estimated_duration_minutes)
      setIsPublished(data.is_published)
    }
    if (error) console.error('Error loading content:', error)
    setLoading(false)
  }

  const handleSaveMetadata = async () => {
    if (!contentId) return
    setSaving(true)
    try {
      const { error } = await updateWorkshopContent(contentId, {
        title, title_en: titleFr, description,
        estimated_duration_minutes: duration,
        is_published: isPublished
      })
      if (error) console.error('Error saving:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleAddBlock = async (blockType: WorkshopBlockType) => {
    if (!contentId) return
    setAddingBlock(true)
    try {
      const nextOrder = blocks.length > 0 ? Math.max(...blocks.map(b => b.sort_order)) + 1 : 0
      const { data, error } = await createWorkshopBlock({
        workshop_content_id: contentId,
        sort_order: nextOrder,
        block_type: blockType,
        title: BLOCK_TYPE_LABELS[blockType],
        config: {},
        estimated_minutes: 5
      })
      if (data) {
        setBlocks(prev => [...prev, data])
        setExpandedBlock(data.id)
      }
      if (error) console.error('Error adding block:', error)
    } finally {
      setAddingBlock(false)
    }
  }

  const handleUpdateBlock = async (blockId: string, updates: Partial<WorkshopBlock>) => {
    const { error } = await updateWorkshopBlock(blockId, updates)
    if (!error) {
      setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, ...updates } : b))
    }
  }

  const handleDeleteBlock = async (blockId: string) => {
    const { error } = await deleteWorkshopBlock(blockId)
    if (!error) {
      setBlocks(prev => prev.filter(b => b.id !== blockId))
      if (expandedBlock === blockId) setExpandedBlock(null)
    }
  }

  const handleMoveBlock = async (blockId: string, direction: 'up' | 'down') => {
    const idx = blocks.findIndex(b => b.id === blockId)
    if (idx < 0) return
    if (direction === 'up' && idx === 0) return
    if (direction === 'down' && idx === blocks.length - 1) return

    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    const newBlocks = [...blocks]
    ;[newBlocks[idx], newBlocks[swapIdx]] = [newBlocks[swapIdx], newBlocks[idx]]

    const reordered = newBlocks.map((b, i) => ({ ...b, sort_order: i }))
    setBlocks(reordered)
    await reorderWorkshopBlocks(reordered.map((b, i) => ({ id: b.id, sort_order: i })))
  }

  const handleDeleteContent = async () => {
    if (!contentId) return
    setDeletingContent(true)
    try {
      const { error } = await deleteWorkshopContent(contentId)
      if (!error) navigate('/admin/workshop-content')
    } finally {
      setDeletingContent(false)
    }
  }

  const totalMinutes = blocks.reduce((sum, b) => sum + (b.estimated_minutes || 0), 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055A4]" />
      </div>
    )
  }

  if (!content) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Contenido no encontrado</p>
        <button onClick={() => navigate('/admin/workshop-content')} className="mt-4 text-[#0055A4] hover:underline">
          Volver
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/admin/workshop-content')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Editar material de taller</h1>
          <p className="text-gray-500 text-sm">
            {(content.unit as any)?.name_es || (content.unit as any)?.title || 'Sin unidad'} — {totalMinutes} min estimados
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setIsPublished(!isPublished); handleSaveMetadata() }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
              isPublished
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
            }`}
          >
            {isPublished ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {isPublished ? 'Publicado' : 'Borrador'}
          </button>
        </div>
      </div>

      {/* Metadata */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <h2 className="font-semibold text-gray-900">Información general</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="content-title-es" className="block text-sm font-medium text-gray-600 mb-1">Título (Español)</label>
            <input id="content-title-es" type="text" value={title} onChange={e => setTitle(e.target.value)}
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900" />
          </div>
          <div>
            <label htmlFor="content-title-fr" className="block text-sm font-medium text-gray-600 mb-1">Título (Francés)</label>
            <input id="content-title-fr" type="text" value={titleFr} onChange={e => setTitleFr(e.target.value)}
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900" />
          </div>
          <div>
            <label htmlFor="content-description" className="block text-sm font-medium text-gray-600 mb-1">Descripción</label>
            <textarea id="content-description" value={description} onChange={e => setDescription(e.target.value)} rows={2}
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 resize-none" />
          </div>
          <div>
            <label htmlFor="content-duration" className="block text-sm font-medium text-gray-600 mb-1">Duración estimada (min)</label>
            <input id="content-duration" type="number" value={duration} onChange={e => setDuration(Number(e.target.value))}
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900" />
          </div>
        </div>
        <button
          onClick={handleSaveMetadata} disabled={saving}
          className="px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] disabled:opacity-50 inline-flex items-center gap-2 font-medium"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar información
        </button>
      </div>

      {/* Bloques */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Bloques del taller ({blocks.length})</h2>
          <span className="text-sm text-gray-500">{totalMinutes} min total</span>
        </div>

        {blocks.map((block, idx) => (
          <BlockCard
            key={block.id}
            block={block}
            index={idx}
            isFirst={idx === 0}
            isLast={idx === blocks.length - 1}
            isExpanded={expandedBlock === block.id}
            onToggle={() => setExpandedBlock(expandedBlock === block.id ? null : block.id)}
            onUpdate={(updates) => handleUpdateBlock(block.id, updates)}
            onDelete={() => handleDeleteBlock(block.id)}
            onMove={(dir) => handleMoveBlock(block.id, dir)}
          />
        ))}

        {blocks.length === 0 && (
          <div className="text-center py-8 bg-white rounded-xl border border-gray-200 border-dashed">
            <p className="text-gray-500">Sin bloques. Agrega el primero abajo.</p>
          </div>
        )}
      </div>

      {/* Agregar bloque */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Agregar bloque
        </h3>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(BLOCK_TYPE_LABELS) as WorkshopBlockType[]).map(type => (
            <button
              key={type}
              onClick={() => handleAddBlock(type)}
              disabled={addingBlock}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors hover:opacity-80 disabled:opacity-50 ${BLOCK_TYPE_COLORS[type]}`}
            >
              {BLOCK_TYPE_LABELS[type]}
            </button>
          ))}
        </div>
      </div>

      {/* Zona de peligro */}
      <div className="bg-red-50 rounded-xl border border-red-200 p-4">
        <h2 className="font-semibold text-red-600 flex items-center gap-2 mb-2">
          <AlertTriangle className="w-5 h-5" />
          Zona de peligro
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Eliminar este material eliminará todos los bloques asociados.
        </p>
        <button
          onClick={() => setShowDeleteContent(true)}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium inline-flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Eliminar material completo
        </button>
      </div>

      {/* Modal eliminar */}
      {showDeleteContent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full border border-gray-200">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">¿Eliminar material?</h3>
              <p className="text-sm text-gray-500 mt-2">
                Se eliminarán {blocks.length} bloques. Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteContent(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                disabled={deletingContent}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteContent}
                disabled={deletingContent}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {deletingContent ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================
// BlockCard
// ============================================
function BlockCard({
  block, index, isFirst, isLast, isExpanded,
  onToggle, onUpdate, onDelete, onMove
}: {
  block: WorkshopBlock
  index: number
  isFirst: boolean
  isLast: boolean
  isExpanded: boolean
  onToggle: () => void
  onUpdate: (updates: Partial<WorkshopBlock>) => void
  onDelete: () => void
  onMove: (dir: 'up' | 'down') => void
}) {
  const [localTitle, setLocalTitle] = useState(block.title || '')
  const [localSection, setLocalSection] = useState(block.section || '')
  const [localInstructions, setLocalInstructions] = useState(block.instructions || '')
  const [localNotes, setLocalNotes] = useState(block.teacher_notes || '')
  const [localMinutes, setLocalMinutes] = useState(block.estimated_minutes || 5)
  const [localConfig, setLocalConfig] = useState(JSON.stringify(block.config, null, 2))
  const [configError, setConfigError] = useState('')
  const [showDelete, setShowDelete] = useState(false)
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setConfigError('')
    let parsedConfig: Record<string, unknown>
    try {
      parsedConfig = JSON.parse(localConfig)
    } catch {
      setConfigError('JSON inválido')
      return
    }

    setSaving(true)
    await onUpdate({
      title: localTitle,
      section: localSection || null,
      instructions: localInstructions || null,
      teacher_notes: localNotes || null,
      estimated_minutes: localMinutes,
      config: parsedConfig
    })
    setSaving(false)
  }

  const colorClass = BLOCK_TYPE_COLORS[block.block_type]

  return (
    <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${isExpanded ? 'ring-1 ring-purple-500' : ''}`}>
      {/* Header del bloque */}
      <div
        className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-gray-50"
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle() }}
      >
        <GripVertical className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <span className="text-sm text-gray-500 font-mono w-6">{index + 1}</span>
        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${colorClass}`}>
          {BLOCK_TYPE_LABELS[block.block_type]}
        </span>
        <span className="font-medium text-gray-900 flex-1 truncate">{block.title || 'Sin título'}</span>
        {block.section && (
          <span className="text-xs text-gray-500 hidden sm:inline">{block.section}</span>
        )}
        <span className="text-xs text-gray-500">{block.estimated_minutes}min</span>
        {/* ✅ span en lugar de div — solo stopPropagation */}
        <span className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => onMove('up')} disabled={isFirst}
            className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            onClick={() => onMove('down')} disabled={isLast}
            className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </span>
      </div>

      {/* Body expandido */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="block-title" className="block text-sm font-medium text-gray-600 mb-1">Título del bloque</label>
              <input id="block-title" type="text" value={localTitle} onChange={e => setLocalTitle(e.target.value)}
                className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm" />
            </div>
            <div>
              <label htmlFor="block-section" className="block text-sm font-medium text-gray-600 mb-1">Sección</label>
              <select id="block-section" value={localSection} onChange={e => setLocalSection(e.target.value)}
                className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm">
                <option value="">Sin sección</option>
                {SECTION_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="block-minutes" className="block text-sm font-medium text-gray-600 mb-1">Duración (min)</label>
              <input id="block-minutes" type="number" value={localMinutes} onChange={e => setLocalMinutes(Number(e.target.value))}
                className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm"
                min={1} max={30} />
            </div>
          </div>

          <div>
            <label htmlFor="block-instructions" className="block text-sm font-medium text-gray-600 mb-1">Instrucciones para el profesor</label>
            <textarea id="block-instructions" value={localInstructions} onChange={e => setLocalInstructions(e.target.value)} rows={2}
              placeholder="Instrucciones que solo ve el profesor..."
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm resize-none" />
          </div>

          <div>
            <label htmlFor="block-notes" className="block text-sm font-medium text-gray-600 mb-1">Notas del profesor</label>
            <textarea id="block-notes" value={localNotes} onChange={e => setLocalNotes(e.target.value)} rows={2}
              placeholder="Notas privadas..."
              className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm resize-none" />
          </div>

          <div>
            {/* ✅ span en lugar de label — tiene icono inline */}
            <span className="block text-sm font-medium text-gray-600 mb-1 flex items-center gap-1.5">
              <Code className="w-4 h-4" />
              Configuración (JSON) — tipo: {block.block_type}
            </span>
            <textarea value={localConfig} onChange={e => { setLocalConfig(e.target.value); setConfigError('') }}
              rows={12}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-green-700 text-sm font-mono resize-y"
              spellCheck={false} />
            {configError && <p className="text-sm text-red-600 mt-1">{configError}</p>}
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowDelete(true)}
              className="px-3 py-1.5 text-red-600 hover:bg-red-100 rounded-lg text-sm inline-flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Eliminar bloque
            </button>
            <button
              onClick={handleSave} disabled={saving}
              className="px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] disabled:opacity-50 inline-flex items-center gap-2 font-medium text-sm"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar bloque
            </button>
          </div>

          {showDelete && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
              <p className="text-sm text-red-300">¿Eliminar este bloque?</p>
              <div className="flex gap-2">
                <button onClick={() => setShowDelete(false)} className="px-3 py-1 bg-gray-100 text-gray-600 rounded text-sm">
                  No
                </button>
                <button onClick={onDelete} className="px-3 py-1 bg-red-600 text-white rounded text-sm">
                  Sí, eliminar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}